import React, { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { useAuth } from "@/contexts/AuthContext";
import { lazyModal } from "./lazyModal";

const AuthModal = lazyModal(
  () => import("./auth/AuthModal"),
  () => useAuth().isAuthModalOpen,
);

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine activePage from location.pathname
  const getActivePage = (pathname) => {
    if (pathname === "/") return "home";
    if (pathname.startsWith("/brands")) return "brands";
    if (pathname.startsWith("/the-badge/the-problem") || pathname.startsWith("/the-problem")) return "trust-problem";
    if (pathname.startsWith("/the-badge")) return "the-badge";
    if (pathname.startsWith("/for-business")) return "for-businesses";
    if (pathname.startsWith("/trust-issue")) return "trust-issue";
    if (pathname.startsWith("/profile")) return "profile";
    return "home";
  };

  const activePage = getActivePage(location.pathname);

  // The site now opens immediately without the former logo intro sequence.
  const [animationPhase, setAnimationPhase] = useState("docked");
  const [isBackdropFading, setIsBackdropFading] = useState(true);
  const [uiRevealed, setUiRevealed] = useState(true);
  const [titleTransform, setTitleTransform] = useState("");
  const [titleTransition, setTitleTransition] = useState("none");

  // Search & filter state
  const [brandSearchQuery, setBrandSearchQuery] = useState(searchParams.get("q") || "");
  const [brandCategory, setBrandCategory] = useState(searchParams.get("category") || "All Brands");
  const [brandOnlyVerified, setBrandOnlyVerified] = useState(searchParams.get("verified") !== "false");

  // Sync state with URL params when on /brands
  useEffect(() => {
    if (activePage === "brands") {
      const q = searchParams.get("q");
      const cat = searchParams.get("category");
      const verified = searchParams.get("verified");
      if (q !== null) setBrandSearchQuery(q);
      if (cat !== null) setBrandCategory(cat);
      if (verified !== null) setBrandOnlyVerified(verified !== "false");
    }
  }, [searchParams, activePage]);

  const heroSearchInputRef = useRef(null);
  const titleRef = useRef(null);
  const animationTimersRef = useRef([]);

  const clearAnimationTimers = () => {
    animationTimersRef.current.forEach((t) => clearTimeout(t));
    animationTimersRef.current = [];
  };

  // Scroll lock during intro sequence on home
  useEffect(() => {
    if (animationPhase !== "docked" && activePage === "home") {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [animationPhase, activePage]);

  // activePage is "home" for pages it doesn't recognise (/privacy, /terms, /about), so the
  // effect below doesn't fire when moving between them. Reset scroll on every path change.
  const hashRef = useRef(location.hash);
  hashRef.current = location.hash;
  useEffect(() => {
    if (!hashRef.current) window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  // If landing on or switching to non-home page, skip intro immediately
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (activePage !== "home") {
      clearAnimationTimers();
      setAnimationPhase("docked");
      setIsBackdropFading(true);
      setUiRevealed(true);
      setTitleTransition("none");
      setTitleTransform("none");
      document.body.style.overflow = "";
    }
  }, [activePage]);

  useEffect(() => () => clearAnimationTimers(), []);

  const handleNavigate = (page, options = {}) => {
    let path = "/";
    if (page === "home" || page === "/") {
      path = "/";
    } else if (page === "brands" || page === "/brands") {
      path = "/brands";
    } else if (page === "the-badge" || page === "/the-badge") {
      path = "/the-badge/things-to-know";
    } else if (page === "the-problem" || page === "/the-problem" || page === "trust-problem") {
      path = "/the-badge/the-problem";
    } else if (
      page === "for-businesses" ||
      page === "for-business" ||
      page === "/for-businesses" ||
      page === "/for-business"
    ) {
      window.open("/business", "_blank", "noopener,noreferrer");
      return;
    } else if (page === "trust-issue" || page === "/trust-issue") {
      path = "/trust-issue";
    } else if (typeof page === "string" && page.startsWith("/")) {
      path = page;
    }

    const params = new URLSearchParams();
    if (options.q) params.set("q", options.q);
    if (options.category && options.category !== "All Brands") params.set("category", options.category);
    if (options.verified !== undefined && !options.verified) params.set("verified", "false");

    const searchStr = params.toString();
    const finalUrl = searchStr ? `${path}?${searchStr}` : path;

    navigate(finalUrl);
  };

  const handleReplay = () => {
    if (activePage !== "home" || location.pathname !== "/") {
      handleNavigate("home");
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearch = (query) => {
    setBrandSearchQuery(query);
    handleNavigate("brands", { q: query });
  };

  const handleFocusSearch = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      if (heroSearchInputRef.current) {
        heroSearchInputRef.current.focus();
      }
    }, 250);
  };

  const handleSearchTermChange = (term) => {
    setBrandSearchQuery(term);
    if (activePage === "brands") {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (term) next.set("q", term);
          else next.delete("q");
          return next;
        },
        { replace: true },
      );
    }
  };

  const handleCategoryChange = (cat) => {
    setBrandCategory(cat);
    if (activePage === "brands") {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (cat && cat !== "All Brands") next.set("category", cat);
          else next.delete("category");
          return next;
        },
        { replace: true },
      );
    }
  };

  const handleOnlyVerifiedChange = (val) => {
    setBrandOnlyVerified(val);
    if (activePage === "brands") {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!val) next.set("verified", "false");
          else next.delete("verified");
          return next;
        },
        { replace: true },
      );
    }
  };

  const isDocked = animationPhase === "docked";

  return (
    <div className="site-shell min-h-screen w-full max-w-[100vw] bg-brand-bg text-brand-obsidian font-body relative box-border antialiased">
      {/* Cinematic Solid Backdrop during intro */}
      {!isDocked && (
        <div
          className={`fixed inset-0 w-screen h-screen bg-brand-bg z-[9998] pointer-events-none transition-opacity duration-700 ${
            isBackdropFading ? "opacity-0" : "opacity-100"
          }`}
          aria-hidden="true"
        />
      )}

      {/* Persistent & Sticky Header / Navbar */}
      <Navbar
        titleRef={titleRef}
        titleTransform={titleTransform}
        titleTransition={titleTransition}
        uiRevealed={uiRevealed}
        onReplay={handleReplay}
        onNavigate={handleNavigate}
        activePage={activePage}
        searchTerm={brandSearchQuery}
        selectedCategory={brandCategory}
        onlyVerified={brandOnlyVerified}
        onFocusSearch={handleFocusSearch}
      />

      {/* Page Content View */}
      <div
        className={`site-content transition-opacity duration-500 ${uiRevealed ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      >
        <Outlet
          context={{
            isAnimationComplete: isDocked,
            onSearch: handleSearch,
            onNavigate: handleNavigate,
            brandSearchQuery,
            setBrandSearchQuery: handleSearchTermChange,
            brandCategory,
            setBrandCategory: handleCategoryChange,
            brandOnlyVerified,
            setBrandOnlyVerified: handleOnlyVerifiedChange,
            heroSearchInputRef,
          }}
        />
      </div>

      {/* Global Shared Footer (hidden on profile pages) */}
      {!location.pathname.startsWith("/profile") && <Footer onNavigate={handleNavigate} />}

      {/* Global Auth Modal (OTP + Google) */}
      <AuthModal />
    </div>
  );
}
